import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { dailyCareLogsApi, DailyCareLogListRow } from '../api/dailyCareLogs';

type ResidentLite = { id: number; name: string };
type StaffLite = { id: number; name: string };

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const DailyCareLogs: React.FC = () => {
  const navigate = useNavigate();

  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(todayStr());
  const [residentId, setResidentId] = useState<string>('');
  const [carerId, setCarerId] = useState<string>('');
  const [concernFilter, setConcernFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 25;

  const [logs, setLogs] = useState<DailyCareLogListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [residents, setResidents] = useState<ResidentLite[]>([]);
  const [staff, setStaff] = useState<StaffLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Load residents and staff once for filter dropdowns
  useEffect(() => {
    (async () => {
      try {
        const [rRes, rStaff] = await Promise.all([
          api.get('/residents'),
          api.get('/users/staff?page=1&limit=200')
        ]);
        const rList: ResidentLite[] =
          (rRes.data?.data?.residents || rRes.data?.data || []).map((r: any) => ({
            id: r.id,
            name: r.name
          }));
        setResidents(rList);
        const sList: StaffLite[] =
          (rStaff.data?.data?.staff || rStaff.data?.data || []).map((u: any) => ({
            id: u.id,
            name: u.name
          }));
        setStaff(sList);
      } catch (e) {
        // non-fatal; filters still work as free text
      }
    })();
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await dailyCareLogsApi.adminListLogs({
        from,
        to,
        residentId: residentId ? Number(residentId) : undefined,
        carerId: carerId ? Number(carerId) : undefined,
        concernFlag: concernFilter ? concernFilter === 'true' : undefined,
        page,
        limit
      });
      setLogs(r.data);
      setTotal(r.pagination.total);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [from, to, residentId, carerId, concernFilter, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const url = dailyCareLogsApi.exportPdfUrl({
        from,
        to,
        residentId: residentId ? Number(residentId) : undefined,
        carerId: carerId ? Number(carerId) : undefined
      });
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `daily-care-logs-${from}-to-${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e: any) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Daily Care Logs</h1>
          <p className="text-sm text-gray-600 mt-1">Browse, filter, and export all daily care submissions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/daily-care-logs/compliance')}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Compliance
          </button>
          <button
            onClick={() => navigate('/daily-care-logs/categories')}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Manage Categories
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={e => { setFrom(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={e => { setTo(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Resident</label>
            <select
              value={residentId}
              onChange={e => { setResidentId(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All residents</option>
              {residents.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Carer</label>
            <select
              value={carerId}
              onChange={e => { setCarerId(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All carers</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Concern</label>
            <select
              value={concernFilter}
              onChange={(e) => { setConcernFilter(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Concerns</option>
              <option value="true">Flagged Only</option>
              <option value="false">No Concern</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchLogs}
              className="w-full px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-black"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resident</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Carer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Concern</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm">Loading…</td></tr>
            )}
            {!loading && logs.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm">No logs found for this filter</td></tr>
            )}
            {!loading && logs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{log.logDate}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {log.logTime ? new Date(log.logTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 font-medium">{log.residentName || `#${log.residentId}`}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{log.carerName || `#${log.carerId}`}</td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{log.generalNotes || '—'}</td>
                <td className="px-4 py-3 text-sm">
                  {log.concernFlag ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      ⚠️ Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ No
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/daily-care-logs/${log.id}`)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View
                    </button>
                    <button
                      onClick={() => navigate(`/daily-care-logs/${log.id}`)}
                      className="text-purple-600 hover:text-purple-800 font-medium"
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <span className="text-sm text-gray-600">
          Page {page} of {totalPages} — {total} logs
        </span>
        <div className="flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50"
          >
            Prev
          </button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyCareLogs;
